import { Component, h, render } from '@monygroupcorp/microact';
import { TransactionOptions } from './TransactionOptions.js';
import { MessagePopup } from '../Util/MessagePopup.js';
import { PriceDisplay } from '../Display/PriceDisplay.js';
import { ApprovalModal } from '../Modal/ApprovalModal.js';
import { SwapInputs } from './SwapInputs.js';
import SwapButton from './SwapButton.js';

export class SwapInterface extends Component {
    constructor(props) {
        super(props);

        this.blockchainService = props.blockchainService;
        this.eventBus = props.eventBus;

        this.state = {
            direction: props.direction || 'buy',
            ethAmount: '',
            execAmount: '',
            activeInput: null,
            freeMint: props.freeMint || false,
            freeSupply: props.freeSupply || 0,
            calculatingAmount: false,
            isPhase2: props.isPhase2 === null ? null : props.isPhase2,
            dataReady: props.dataReady || false,
            balances: props.balances || { eth: '0', exec: '0' },
            price: props.price || { current: 0 },
            contractData: props.contractData || {},
        };

        this._address = props.address || null;
        this.messagePopup = new MessagePopup('status-message');
        this.messagePopup.initialize?.();
        this.calculateTimer = null;
        this.onDirectionChange = props.onDirectionChange;
        this.transactionOptionsState = {
            message: '',
            nftMintingEnabled: false
        };
        this.approveModal = null;
        this.eventListeners = [];
        this.instanceId = Math.random().toString(36).substring(2, 9);
        console.log(`[SwapInterface] Instance created: ${this.instanceId}`);
    }

    handleBalanceUpdate(update) {
        const newBalances = update.balances || this.props.balances;
        const newFreeMint = update.freeMint !== undefined ? update.freeMint : this.props.freeMint;
        const newFreeSupply = update.freeSupply !== undefined ? update.freeSupply : this.props.freeSupply;

        this.setState({
            balances: newBalances,
            freeMint: newFreeMint,
            freeSupply: newFreeSupply
        });
    }

    async calculateSwapAmount(amount, inputType) {
        if (!amount || isNaN(parseFloat(amount))) {
            return '';
        }

        try {
            if (this.isLiquidityDeployed()) {
                const price = this.state.price.current;
                if (inputType === 'eth') {
                    const ethAmount = parseFloat(amount);
                    const execAmount = (ethAmount / price * 1000000) * 0.95;
                    return execAmount.toFixed(0);
                } else {
                    const execAmount = parseFloat(amount);
                    const ethAmount = (execAmount / 1000000) * price * 1.055;
                    return ethAmount.toFixed(6);
                }
            } else {
                if (inputType === 'eth') {
                    const execAmount = await this.blockchainService.getExecForEth(amount);
                    const { freeSupply, freeMint } = this.state;
                    const freeMintBonus = (freeSupply > 0 && !freeMint) ? 1000000 : 0;
                    return Math.floor(execAmount + freeMintBonus).toString();
                } else {
                    const ethAmount = await this.blockchainService.getEthForExec(amount);
                    return ethAmount.toString();
                }
            }
        } catch (error) {
            console.error('Error calculating swap amount:', error);
            return '';
        }
    }

    didMount() {
        console.log(`[${this.instanceId}] SwapInterface didMount called`);
        this.bindEvents();

        const eventSubscriptions = [
            ['contractData:updated', this.bind(this.handleContractDataUpdate), 'high'],
            ['transaction:pending', this.bind(this.handleTransactionEvents), 'normal'],
            ['transaction:confirmed', this.bind(this.handleTransactionEvents), 'normal'],
            ['transaction:success', this.bind(this.handleTransactionEvents), 'normal'],
            ['transaction:error', this.bind(this.handleTransactionEvents), 'normal'],
            ['balances:updated', this.bind(this.handleBalanceUpdate), 'high'],
            ['transactionOptions:update', this.bind(this.handleTransactionOptionsUpdate), 'low']
        ];

        this.eventListeners = eventSubscriptions.map(([event, handler]) => {
            return this.eventBus.on(event, handler);
        });

        const { contractData } = this.state;
        if (contractData && contractData.liquidityPool !== undefined) {
            const isPhase2 = this.isLiquidityDeployed();
            this.setState({
                isPhase2: isPhase2,
                dataReady: true
            });
        }
    }

    willUnmount() {
        console.log(`[${this.instanceId}] SwapInterface willUnmount called`);
        this.eventListeners.forEach(unsubscribe => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        });
        this.eventListeners = [];

        if (this.approveModal) {
            try {
                this.approveModal.handleClose();
            } catch (e) {
                console.warn('Error closing approval modal during unmount:', e);
            }
            this.approveModal = null;
        }

        if (this.calculateTimer) {
            clearTimeout(this.calculateTimer);
            this.calculateTimer = null;
        }
    }

    bindEvents() {
        // Events are bound inline in render
    }

    handleTransactionEvents(event) {
        if (event?.handledBy?.includes(this.instanceId)) {
            return;
        }
        if (!event.handledBy) {
            event.handledBy = [];
        }
        event.handledBy.push(this.instanceId);

        const direction = this.state.direction === 'buy' ? 'Buy' : 'Sell';

        if (!event || !event.type) {
            return;
        }

        if ((event.type === 'buy' || event.type === 'sell' || event.type === 'swap') && !event.error) {
            this.messagePopup.info(`${direction} transaction. Simulating...`, 'Transaction Pending');
        }

        if (event.hash) {
            this.messagePopup.info('Transaction confirmed, waiting for completion...', 'Transaction Confirmed');
        }

        if (event.receipt && (event.type === 'buy' || event.type === 'sell' || event.type === 'swap')) {
            const amount = this.state.direction === 'buy'
                ? this.state.execAmount + ' EXEC'
                : this.state.ethAmount + ' ETH';

            this.messagePopup.success(
                `Successfully ${direction.toLowerCase() === 'buy' ? 'bought' : 'sold'} ${amount}`,
                'Transaction Complete'
            );

            this.setState({
                ethAmount: '',
                execAmount: '',
                calculatingAmount: false
            });
        }

        if (event.error && !event.handled) {
            let errorMessage = event.error?.message || 'Transaction failed';
            if (errorMessage.includes('Contract call')) {
                const parts = errorMessage.split(': ');
                errorMessage = parts[parts.length - 1];
            }
            const context = this.state.direction === 'buy' ? 'Buy Failed' : 'Sell Failed';
            this.messagePopup.error(`${context}: ${errorMessage}`, 'Transaction Failed');
            event.handled = true;
        }
    }

    handleTransactionOptionsUpdate(options) {
        this.transactionOptionsState = {
            message: options.message,
            nftMintingEnabled: options.nftMintingEnabled
        };
    }

    handleInput(inputType, value) {
        if (this.calculateTimer) {
            clearTimeout(this.calculateTimer);
        }

        const newState = {
            activeInput: inputType,
            calculatingAmount: true
        };

        if (this.state.direction === 'buy') {
            if (inputType === 'top') {
                newState.ethAmount = value;
            } else {
                newState.execAmount = value;
            }
        } else {
            if (inputType === 'top') {
                newState.execAmount = value;
            } else {
                newState.ethAmount = value;
            }
        }

        this.setState(newState);

        this.calculateTimer = setTimeout(async () => {
            try {
                const isEthInput = (this.state.direction === 'buy') === (inputType === 'top');
                const calculatedAmount = await this.calculateSwapAmount(value, isEthInput ? 'eth' : 'exec');

                if (isEthInput) {
                    this.setState({ execAmount: calculatedAmount, calculatingAmount: false });
                } else {
                    this.setState({ ethAmount: calculatedAmount, calculatingAmount: false });
                }
            } catch (error) {
                console.error('Error calculating swap amount:', error);
                this.setState({ calculatingAmount: false });
            }
        }, 750);
    }

    handleDirectionSwitch(e) {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (this.calculateTimer) {
            clearTimeout(this.calculateTimer);
        }

        const newDirection = this.state.direction === 'buy' ? 'sell' : 'buy';

        this.setState({
            direction: newDirection,
            calculatingAmount: false,
            activeInput: null
        });

        if (this.onDirectionChange) {
            this.onDirectionChange(newDirection);
        }
    }

    handleQuickFill(amount, percentage) {
        let value;

        if (amount) {
            value = amount;
        } else if (percentage) {
            const { balances } = this.state;
            const execBalance = balances.exec;

            if (!execBalance || execBalance === '0') {
                return;
            }

            let readableBalance = BigInt(execBalance) / BigInt(1e18);

            if (this.state.freeMint) {
                readableBalance = readableBalance - BigInt(1000000);
                if (readableBalance <= 0) {
                    this.messagePopup.info('You only have free mint tokens which cannot be sold.', 'Cannot Quick Fill');
                    return;
                }
            }

            const fillAmount = (readableBalance * BigInt(percentage)) / BigInt(100);
            value = fillAmount.toString();
        }

        this.handleInput('top', value);
    }

    isLiquidityDeployed() {
        const { contractData } = this.state;
        if (!contractData || !contractData.liquidityPool) {
            return false;
        }
        return contractData.liquidityPool !== '0x0000000000000000000000000000000000000000';
    }

    async handleSwap() {
        try {
            if (this.state.calculatingAmount) {
                this.messagePopup.info('Please wait for the calculation to complete', 'Loading');
                return;
            }

            const { ethAmount, execAmount, direction, balances, freeMint } = this.state;

            if (!ethAmount || !execAmount || parseFloat(ethAmount) <= 0 || parseFloat(execAmount) <= 0) {
                this.messagePopup.info('Please enter valid amounts', 'Invalid Input');
                return;
            }

            if (direction === 'buy') {
                let ethBalance = parseFloat(this.blockchainService.formatEther(balances.eth || '0'));
                const ethNeeded = parseFloat(ethAmount);

                if (isNaN(ethNeeded) || isNaN(ethBalance) || ethNeeded > ethBalance) {
                    this.messagePopup.info(`Not enough ETH balance. You have ${ethBalance.toFixed(6)} ETH, need ${ethNeeded} ETH`, 'Insufficient Balance');
                    return;
                }
            } else {
                const execAmountClean = execAmount.replace(/,/g, '');
                const execBalance = BigInt(balances.exec || 0);
                const execNeeded = BigInt(parseInt(execAmountClean) || 0);

                if (execNeeded > execBalance) {
                    const execBalanceFormatted = parseInt(balances.exec || 0).toLocaleString();
                    const execNeededFormatted = parseInt(execAmountClean).toLocaleString();
                    this.messagePopup.info(`Not enough EXEC balance. You have ${execBalanceFormatted} EXEC, need ${execNeededFormatted} EXEC`, 'Insufficient Balance');
                    return;
                }
            }

            if (direction === 'sell' && parseInt(execAmount.replace(/,/g, '')) <= 1000000 && freeMint) {
                this.messagePopup.info('Free minted tokens cannot be sold directly.', 'Free Mint Restriction');
                return;
            }

            const isLiquidityDeployed = this.isLiquidityDeployed();
            const cleanExecAmount = this.state.execAmount.replace(/,/g, '');

            if (isLiquidityDeployed) {
                const ethValue = this.blockchainService.parseEther(this.state.ethAmount);
                const execAmountBI = this.blockchainService.parseExec(cleanExecAmount);
                const address = await this.getAddress();

                if (!address) {
                    this.messagePopup.error('No wallet address available. Please reconnect your wallet.', 'Wallet Error');
                    return;
                }

                if (this.state.direction === 'buy') {
                    await this.blockchainService.swapExactEthForTokenSupportingFeeOnTransfer(address, { amount: execAmountBI }, ethValue);
                } else {
                    const routerAddress = this.blockchainService.swapRouter?.address || this.blockchainService.swapRouterAddress;
                    const routerAllowance = await this.blockchainService.getApproval(address, routerAddress);

                    if (BigInt(routerAllowance) < BigInt(execAmountBI)) {
                        if (this.approveModal) {
                            try {
                                this.eventBus.off('approve:complete');
                                this.approveModal.handleClose();
                            } catch (e) { }
                            this.approveModal = null;
                        }

                        const modalRoot = document.createElement('div');
                        document.body.appendChild(modalRoot);

                        render(h(ApprovalModal, {
                            amount: cleanExecAmount,
                            blockchainService: this.blockchainService,
                            userAddress: address,
                            eventBus: this.eventBus,
                            ref: instance => this.approveModal = instance
                        }), modalRoot);

                        this.eventBus.once('approve:complete', async () => {
                            try {
                                await this.blockchainService.swapExactTokenForEthSupportingFeeOnTransferV2(address, { amount: execAmountBI });
                            } catch (error) {
                                this.messagePopup.error(`Swap Failed: ${error.message}`, 'Transaction Failed');
                            }
                        });

                        this.eventBus.once('approveModal:closed', () => {
                            if (modalRoot.parentNode) {
                                modalRoot.parentNode.removeChild(modalRoot);
                            }
                            this.approveModal = null;
                        });

                        this.approveModal?.show();
                        return;
                    }

                    await this.blockchainService.swapExactTokenForEthSupportingFeeOnTransferV2(address, { amount: execAmountBI });
                }
            } else {
                let proof;
                try {
                    const currentTier = await this.blockchainService.getCurrentTier();
                    proof = await this.blockchainService.getMerkleProof(this.address, currentTier);
                    if (!proof) {
                        this.messagePopup.error(`You are not whitelisted for Tier ${currentTier + 1}.`, 'Not Whitelisted');
                        return;
                    }
                } catch (error) {
                    this.messagePopup.error('Failed to verify whitelist status.', 'Whitelist Check Failed');
                    return;
                }

                let adjustedExecAmount = cleanExecAmount;
                if (this.state.direction === 'buy' && this.state.freeSupply > 0 && !this.state.freeMint) {
                    const numAmount = parseInt(cleanExecAmount);
                    adjustedExecAmount = Math.max(0, numAmount - 1000000).toString();
                }

                const ethValue = this.blockchainService.parseEther(this.state.ethAmount);
                const execAmountBI = this.blockchainService.parseExec(adjustedExecAmount);

                if (this.state.direction === 'buy') {
                    await this.blockchainService.buyBonding({
                        amount: execAmountBI,
                        maxCost: ethValue,
                        mintNFT: this.transactionOptionsState.nftMintingEnabled,
                        proof: proof.proof,
                        message: this.transactionOptionsState.message
                    }, ethValue);
                } else {
                    const minReturn = BigInt(ethValue) * BigInt(999) / BigInt(1000);
                    await this.blockchainService.sellBonding({
                        amount: execAmountBI,
                        minReturn: minReturn,
                        proof: proof.proof,
                        message: this.transactionOptionsState.message
                    });
                }
            }
        } catch (error) {
            console.error('Swap failed:', error);
            let errorMessage = error.message;
            if (errorMessage.includes('Contract call')) {
                const parts = errorMessage.split(': ');
                errorMessage = parts[parts.length - 1];
            }
            const context = this.state.direction === 'buy' ? 'Buy Failed' : 'Sell Failed';
            this.messagePopup.error(`${context}: ${errorMessage}`, 'Transaction Failed');
        }
    }

    handleContractDataUpdate() {
        try {
            const previousPhase = this.state.isPhase2;
            const wasDataReady = this.state.dataReady;
            const isPhase2 = this.isLiquidityDeployed();

            if (previousPhase === null || !wasDataReady) {
                this.setState({
                    isPhase2,
                    dataReady: true
                });
            } else if (previousPhase !== isPhase2) {
                this.setState({ isPhase2 });
            }
        } catch (error) {
            console.error('Error in handleContractDataUpdate:', error);
        }
    }

    async getAddress() {
        try {
            return await Promise.resolve(this._address);
        } catch (error) {
            console.error(`[${this.instanceId}] Error resolving address:`, error);
            return null;
        }
    }

    setAddress(newAddress) {
        this._address = newAddress;
    }

    get address() {
        return this._address;
    }

    set address(newAddress) {
        this._address = newAddress;
    }

    render() {
        const { direction, isPhase2, dataReady, balances, freeMint, freeSupply, ethAmount, execAmount, calculatingAmount } = this.state;

        if (!dataReady || isPhase2 === null) {
            return h('div', { className: 'swap-interface' },
                h('div', { className: 'price-display-container' }),
                h('div', { className: 'quick-fill-buttons-container' }),
                h('div', { className: 'swap-inputs-container', style: { padding: '20px', textAlign: 'center' } },
                    'Loading swap interface...'
                ),
                h('div', { className: 'transaction-options-container' }),
                h('div', { className: 'swap-button-container' })
            );
        }

        const formattedEthBalance = parseFloat(balances.eth).toFixed(6);
        const formattedExecBalance = parseInt(balances.exec).toLocaleString();

        return h('div', { className: 'swap-interface' },
            h(PriceDisplay, {
                price: this.state.price,
                contractData: this.state.contractData,
                eventBus: this.eventBus
            }),

            // Free mint notice
            direction === 'sell' && freeMint && !isPhase2 &&
                h('div', { className: 'free-mint-notice' },
                    'You have 1,000,000 $EXEC you received for free that cannot be sold here.'
                ),
            direction === 'buy' && freeSupply > 0 && !freeMint && !isPhase2 &&
                h('div', { className: 'free-mint-notice free-mint-bonus' },
                    '1,000,000 $EXEC will be added to your purchase. Thank you.'
                ),

            // Quick fill buttons
            h('div', { className: 'quick-fill-buttons-container' },
                h('div', { className: 'quick-fill-buttons' },
                    direction === 'buy' ? [
                        h('button', { key: '0.0025', onClick: () => this.handleQuickFill('0.0025') }, '0.0025'),
                        h('button', { key: '0.01', onClick: () => this.handleQuickFill('0.01') }, '0.01'),
                        h('button', { key: '0.05', onClick: () => this.handleQuickFill('0.05') }, '0.05'),
                        h('button', { key: '0.1', onClick: () => this.handleQuickFill('0.1') }, '0.1')
                    ] : [
                        h('button', { key: '25', onClick: () => this.handleQuickFill(null, 25) }, '25%'),
                        h('button', { key: '50', onClick: () => this.handleQuickFill(null, 50) }, '50%'),
                        h('button', { key: '75', onClick: () => this.handleQuickFill(null, 75) }, '75%'),
                        h('button', { key: '100', onClick: () => this.handleQuickFill(null, 100) }, '100%')
                    ]
                )
            ),

            // Swap inputs
            h(SwapInputs, {
                direction,
                ethAmount,
                execAmount,
                calculatingAmount,
                freeMint,
                balances,
                isPhase2,
                onInput: this.bind(this.handleInput)
            }),

            // Direction switch
            h('div', { className: 'direction-switch-container' },
                h('button', {
                    className: 'direction-switch',
                    onClick: this.bind(this.handleDirectionSwitch)
                }, '\u2195')
            ),

            // Transaction options (Phase 1 only)
            !isPhase2 && h(TransactionOptions, {
                eventBus: this.eventBus,
                swapDirection: direction,
                isPhase2
            }),

            // Swap button
            h(SwapButton, {
                direction,
                disabled: calculatingAmount,
                onClick: this.bind(this.handleSwap)
            })
        );
    }
}
